import { faSpinner, faWarning } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as d3 from "d3";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { QuarterEmissions } from "../../../apiClient/generated";
import { useResizeObserver } from "../../../ui/Plot/hooks";
import type { AnalyticsFilters } from "../state";
import { renderPlotHeader } from "../utils/plotUtils";
import {
    createProviderColorScale,
    getAllProviders,
    type StackDataPoint,
    transformToStackData,
} from "./stackUtils";
import { fillMissingQuarters } from "./utils";

interface TooltipData {
    quarter: string;
    providerName: string;
    count: number;
    percentage: number;
    totalCount: number;
}

const ProviderTooltip = ({
    data,
    position,
}: {
    data: TooltipData;
    position: { x: number; y: number };
}) => {
    return (
        <div
            className="fixed bg-white text-black px-2 py-1.5 rounded shadow-lg z-[9999] text-sm min-w-44 pointer-events-none"
            style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
            }}
        >
            <div className="font-semibold mb-1">{data.quarter}</div>
            <div className="font-semibold text-ae-blue-550 mb-1">
                {data.providerName}
            </div>

            <div className="space-y-0 mb-0.5 text-xs">
                <div className="flex justify-between gap-3">
                    <span>Observation Count</span>
                    <span className="font-medium">{data.count}</span>
                </div>
                <div className="flex justify-between gap-3">
                    <span>Total Count</span>
                    <span className="font-medium">{data.totalCount}</span>
                </div>
                <div className="flex justify-between gap-3">
                    <span>Percentage</span>
                    <span className="font-medium">
                        {data.percentage.toFixed(1)}%
                    </span>
                </div>
            </div>
        </div>
    );
};

interface ProviderCountPlotProps {
    svgRef?: React.MutableRefObject<SVGSVGElement | null>;
    emissionsData: QuarterEmissions[];
    loading?: boolean;
    filters: AnalyticsFilters;
}

export const ProviderCountPlot = ({
    svgRef: externalSvgRef,
    emissionsData,
    loading,
    filters,
}: ProviderCountPlotProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const dimensions = useResizeObserver(containerRef);

    // Hover context handling with external state
    const [hoverContext, setHoverContext] = useState<TooltipData | undefined>();
    const [tooltipPosition, setTooltipPosition] = useState<{
        x: number;
        y: number;
    }>({ x: 0, y: 0 });

    // Sync internal ref to external ref for image export
    useEffect(() => {
        if (externalSvgRef && svgRef.current) {
            externalSvgRef.current = svgRef.current;
        }
    }, [externalSvgRef, dimensions]);

    // Zoom state and refs
    const [currentTransform, setCurrentTransform] = useState<d3.ZoomTransform>(
        d3.zoomIdentity,
    );
    const [zoomDomain, setZoomDomain] = useState<{
        x: [number, number] | null;
        y: [number, number] | null;
    }>({
        x: null,
        y: null,
    });
    const zoomBehaviorRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown>>();

    // Reset handler
    const handleReset = useCallback(() => {
        // Reset both zoom transform and domain
        setZoomDomain({ x: null, y: null });
        setCurrentTransform(d3.zoomIdentity);

        if (!svgRef.current || !zoomBehaviorRef.current) return;
        d3.select(svgRef.current)
            .transition()
            .duration(750)
            .call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
    }, []);

    // Extract all unique providers (sorted alphabetically for color consistency)
    const allProviders = useMemo(
        () => getAllProviders(emissionsData || []),
        [emissionsData],
    );

    // Transform to stack-friendly format
    const stackData = useMemo(() => {
        if (!emissionsData || emissionsData.length === 0) return [];

        const transformed = transformToStackData(emissionsData, allProviders);

        // Fill missing quarters with 0 values
        return fillMissingQuarters<StackDataPoint>(transformed, () => {
            const obj: Partial<StackDataPoint> = {
                totalCount: 0,
                _counts: {},
            };
            allProviders.forEach((provider) => {
                obj._counts![provider] = 0;
            });
            return obj as Omit<StackDataPoint, "quarter">;
        });
    }, [emissionsData, allProviders]);

    // Generate D3 stack series
    const series = useMemo(() => {
        if (stackData.length === 0 || allProviders.length === 0) return [];

        return d3
            .stack<StackDataPoint>()
            .keys(allProviders)
            .value((d, key) => d._counts[key] || 0)
            .order(d3.stackOrderDescending) // Smallest at top, largest at bottom
            .offset(d3.stackOffsetNone)(stackData);
    }, [stackData, allProviders]);

    // Create color scale
    const colorScale = useMemo(
        () => createProviderColorScale(allProviders),
        [allProviders],
    );

    // Setup zoom and brush behaviors (runs when dimensions change)
    useEffect(() => {
        if (dimensions.width === 0 || dimensions.height === 0) return;
        if (!svgRef.current) return;

        const svg = d3.select(svgRef.current);

        // Create zoom behavior (double-click only)
        const zoom = d3
            .zoom<SVGSVGElement, unknown>()
            .scaleExtent([1, 10])
            .on("zoom", (event) => {
                setCurrentTransform(event.transform);
            })
            .filter((event) => {
                // Only allow  double-click, disable drag (drag is for brush)
                return event.type === "dblclick";
            });

        zoomBehaviorRef.current = zoom;

        // Apply zoom to SVG
        svg.call(zoom);

        // Setup double-click to reset
        svg.on("dblclick.zoom", () => {
            setZoomDomain({ x: null, y: null });
            setCurrentTransform(d3.zoomIdentity);
            svg.transition()
                .duration(750)
                .call(zoom.transform, d3.zoomIdentity);
        });
    }, [dimensions]);

    // Reset zoom when data changes
    useEffect(() => {
        setZoomDomain({ x: null, y: null });
        setCurrentTransform(d3.zoomIdentity);
        if (!svgRef.current || !zoomBehaviorRef.current) return;
        d3.select(svgRef.current).call(
            zoomBehaviorRef.current.transform,
            d3.zoomIdentity,
        );
    }, [stackData]);

    // Effect 3: Render chart (runs on data/transform/dimension changes)
    useEffect(() => {
        if (!stackData || stackData.length === 0) return;
        if (dimensions.width === 0 || dimensions.height === 0) return;
        if (!series || series.length === 0) return;

        // Clear previous SVG content
        d3.select(svgRef.current).selectAll("*").remove();

        const margin = { top: 65, right: 105, bottom: 35, left: 70 };
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;

        // Create SVG
        const svg = d3
            .select(svgRef.current)
            .attr("viewBox", `0 0 ${dimensions.width} ${dimensions.height}`)
            .attr("width", dimensions.width)
            .attr("height", dimensions.height);

        // Draw header
        renderPlotHeader({
            svg: svg,
            plotTitle: `Number Of Observations With Emissions Exceeding ${filters.detectionLimit.toFixed(0)} kg/h`,
            filters,
        });

        // Create the chart group
        const chart = svg
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // Filter data based on box zoom domain
        let visibleData = stackData;
        if (zoomDomain.x) {
            const allQuarters = stackData.map((d) => d.quarter);
            const startIdx = Math.floor(zoomDomain.x[0]);
            const endIdx = Math.ceil(zoomDomain.x[1]);
            const visibleQuarters = allQuarters.slice(
                Math.max(0, startIdx),
                Math.min(allQuarters.length, endIdx),
            );
            visibleData = stackData.filter((d) =>
                visibleQuarters.includes(d.quarter),
            );
        }

        // Create X scale with zoom transform applied to range
        const xScale = d3
            .scaleBand()
            .domain(visibleData.map((d) => d.quarter))
            .range([0, width].map((d) => currentTransform.applyX(d)))
            .padding(0.2);

        // Create Y scale - calculate max count from visible data
        const maxCount = d3.max(visibleData, (d) => d.totalCount);

        // Determine Y domain (default or zoomed)
        const yDomainMin = zoomDomain.y ? zoomDomain.y[0] : 0;
        const yDomainMax = zoomDomain.y ? zoomDomain.y[1] : maxCount * 1.02;
        const yScale = d3
            .scaleLinear()
            .domain([yDomainMin, yDomainMax])
            .range([height, 0])
            .nice();

        // Create brush for box zoom
        const brush = d3
            .brush()
            .extent([
                [0, 0],
                [width, height],
            ])
            .on("end", (event) => {
                if (!event.selection) return;

                const [[x0, y0], [x1, y1]] = event.selection as [
                    [number, number],
                    [number, number],
                ];

                // Ignore very small selections
                if (Math.abs(x1 - x0) < 10 || Math.abs(y1 - y0) < 10) {
                    chart.select<SVGGElement>(".brush").call(brush.move, null);
                    return;
                }

                // Convert pixel coordinates to data domain
                // For X: map to quarter indices
                // Get current zoom range (or full range if not zoomed)
                const currentStart = zoomDomain.x ? zoomDomain.x[0] : 0;
                const currentEnd = zoomDomain.x
                    ? zoomDomain.x[1]
                    : stackData.length;
                const currentRange = currentEnd - currentStart;

                // Calculate brush selection as fraction of visible area
                const startFraction = x0 / width;
                const endFraction = x1 / width;

                // Map to absolute indices in stackData
                const absoluteStart =
                    currentStart + currentRange * startFraction;
                const absoluteEnd = currentStart + currentRange * endFraction;

                const xDomain: [number, number] = [
                    Math.max(0, Math.floor(absoluteStart)),
                    Math.min(stackData.length, Math.ceil(absoluteEnd)),
                ];

                // For Y: invert scale (SVG y-axis is inverted, so y0 is bottom, y1 is top)
                const yBottom = yScale.invert(y1); // Bottom of selection
                const yTop = yScale.invert(y0); // Top of selection

                // Only zoom if the range is meaningful
                const yRange = yTop - yBottom;
                if (yRange < 1) {
                    chart.select<SVGGElement>(".brush").call(brush.move, null);
                    return;
                }

                setZoomDomain({
                    x: xDomain,
                    y: [Math.max(0, yBottom), yTop], // Allow y-axis to zoom dynamically
                });

                // Clear brush selection after zoom
                chart.select<SVGGElement>(".brush").call(brush.move, null);
            });

        // Attach brush to chart
        const brushGroup = chart.append("g").attr("class", "brush").call(brush);

        // Style brush selection
        brushGroup
            .select(".selection")
            .attr("fill", "#1677ff")
            .attr("fill-opacity", 0.2)
            .attr("stroke", "#1677ff")
            .attr("stroke-width", 1.5)
            .attr("stroke-dasharray", "4,4");

        // Draw Y-axis gridlines
        chart
            .append("g")
            .attr("class", "y-grid")
            .call(
                d3
                    .axisLeft(yScale)
                    .ticks(5)
                    .tickSize(-width)
                    .tickFormat(() => ""),
            )
            .call((g) => g.select(".domain").remove())
            .call((g) =>
                g
                    .selectAll("line")
                    .attr("stroke", "#ccc")
                    .attr("stroke-opacity", 0.15)
                    .attr("shape-rendering", "crispEdges"),
            );

        // Create layer groups (one per provider)
        const layers = chart
            .selectAll(".layer")
            .data(series)
            .join("g")
            .attr("class", "layer")
            .attr("fill", (d) => colorScale(d.key));

        // Create rectangles for each quarter within each layer
        layers
            .selectAll("rect")
            .data((layer) =>
                layer
                    .filter((segment) => {
                        // Only render visible quarters
                        const x = xScale(segment.data.quarter);
                        if (x === undefined) return false;
                        const barWidth = xScale.bandwidth();
                        return x + barWidth >= 0 && x <= width;
                    })
                    .map((segment) => ({
                        segment,
                        providerName: layer.key,
                    })),
            )
            .join("rect")
            .attr("x", (d) => xScale(d.segment.data.quarter) || 0)
            .attr("y", (d) => {
                const yTop = yScale(d.segment[1]);
                // Constrain to not go above chart area
                return Math.max(0, yTop);
            })
            .attr("height", (d) => {
                const yTop = yScale(d.segment[1]);
                const yBottom = yScale(d.segment[0]);
                // Constrain bars to chart boundaries
                const constrainedTop = Math.max(0, yTop);
                const constrainedBottom = Math.min(height, yBottom);
                return Math.max(0, constrainedBottom - constrainedTop);
            })
            .attr("width", xScale.bandwidth())
            .attr("opacity", 1)
            .on("mouseenter", function (_e, d) {
                d3.select(this).attr("opacity", 0.8);

                const count = d.segment.data._counts[d.providerName];
                const totalCount = d.segment.data.totalCount;
                const percentage =
                    totalCount > 0 ? (count / totalCount) * 100 : 0;

                setHoverContext({
                    quarter: d.segment.data.quarter,
                    providerName: d.providerName,
                    count,
                    percentage,
                    totalCount,
                });
            })
            .on("mousemove", (event) => {
                // Update tooltip position to follow cursor
                setTooltipPosition({
                    x: event.pageX + 10,
                    y: event.pageY + 10,
                });
            })
            .on("mouseleave", function () {
                d3.select(this).attr("opacity", 1);
                setHoverContext(undefined);
            });

        // Draw X-axis
        chart
            .append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0,${height})`)
            .call(d3.axisBottom(xScale))
            .selectAll("text")
            .attr("fill", "#666")
            .attr("font-size", "12px");

        // Draw Y-axis
        chart
            .append("g")
            .attr("class", "y-axis")
            .call(
                d3
                    .axisLeft(yScale)
                    .ticks(5)
                    .tickFormat((d) => d3.format(",")(d as number)),
            )
            .selectAll("text")
            .attr("fill", "#666")
            .attr("font-size", "12px");

        // Draw legend (compact layout)
        const legendItemHeight = 16;
        const totalLegendHeight = allProviders.length * legendItemHeight;
        const legend = svg
            .append("g")
            .attr("class", "legend")
            .attr(
                "transform",
                `translate(${width + margin.left + 8}, ${height + margin.top - totalLegendHeight})`,
            );
        const legendItems = legend
            .selectAll(".legend-item")
            .data(allProviders.toReversed())
            .join("g")
            .attr("class", "legend-item")
            .attr(
                "transform",
                (d, i) => `translate(0, ${i * legendItemHeight})`,
            );

        // Color box (smaller)
        legendItems
            .append("rect")
            .attr("width", 12)
            .attr("height", 12)
            .attr("fill", (d) => colorScale(d))
            .attr("opacity", 0.8);

        // Provider name (smaller font, tighter spacing)
        legendItems
            .append("text")
            .attr("x", 16)
            .attr("y", 10)
            .attr("font-size", "10px")
            .attr("fill", "#666")
            .text((d) => (d.length > 14 ? d.substring(0, 11) + "..." : d))
            .append("title")
            .text((d) => d);

        // Add y-axis label
        chart
            .append("text")
            .classed("text-gray-500", true)
            .attr("transform", `rotate(-90) translate(${-height / 2}, -45)`)
            .attr("x", 0)
            .attr("y", 0)
            .attr("text-anchor", "middle")
            .attr("fill", "#666")
            .attr("font-size", "12px")
            .text("Observations Exceeding Threshold");

        // Add x-axis label
        chart
            .append("text")
            .attr("x", width / 2)
            .attr("y", height + 32)
            .attr("fill", "#666")
            .attr("text-anchor", "middle")
            .attr("font-size", "12px")
            .text("Quarters");
    }, [
        dimensions,
        stackData,
        series,
        currentTransform,
        zoomDomain,
        allProviders,
        colorScale,
        filters,
        svgRef,
    ]);

    return (
        <div
            ref={containerRef}
            className="relative flex-grow w-full h-full overflow-hidden"
        >
            <svg
                ref={svgRef}
                className="w-full h-full"
                style={{ cursor: "crosshair" }}
            />
            {/* Reset zoom button - only show when zoomed */}
            {(currentTransform.k !== 1 ||
                currentTransform.x !== 0 ||
                currentTransform.y !== 0 ||
                zoomDomain.x !== null ||
                zoomDomain.y !== null) && (
                <button
                    type="button"
                    onClick={handleReset}
                    className="absolute top-11 right-2 px-3 py-1 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 transition-colors text-sm"
                    title="Reset zoom"
                >
                    Reset Zoom
                </button>
            )}
            {/* Loading overlay */}
            {loading && (
                <div className="absolute inset-0 flex items-center justify-center gap-2 bg-white/90 z-10">
                    <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin"
                    />
                    <div className="text-gray-500">Loading...</div>
                </div>
            )}
            {/* Empty data overlay */}
            {!loading && stackData.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center gap-2 bg-white/90 z-10">
                    <FontAwesomeIcon icon={faWarning} />
                    <div className="text-gray-500">
                        No data available for selected filters
                    </div>
                </div>
            )}
            {/* Tooltip */}
            {hoverContext &&
                createPortal(
                    <ProviderTooltip
                        data={hoverContext}
                        position={tooltipPosition}
                    />,
                    document.body,
                )}
        </div>
    );
};
